import Big from 'big.js';
import { atom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';
import { useMemo } from 'react';
import { PlanRow } from 'modules/campaign/row';
import { GridSummaryRow, RowSetSummaryFields, SummaryFields, TotalBreakdown } from '../types';
import { parseBig } from 'utilities';
import { gridAtom, EntityKey } from './grid';
import { baseRowAtom, usePlanSettings } from './plan';

const zero = Big(0);
const zeroBreakdown: TotalBreakdown = { fee: zero, media: zero, service: zero };

function sumBreakdown(a: TotalBreakdown, b: TotalBreakdown): TotalBreakdown {
  return {
    fee: a.fee.plus(b.fee),
    media: a.media.plus(b.media),
    service: a.service.plus(b.service),
  };
}

const initialSummary: SummaryFields = {
  clientGrossTotal: zero,
  clientTotal: zero,
  clientTotalBreakdown: zeroBreakdown,
  providerTotal: zero,
  providerTotalBreakdown: zeroBreakdown,
  budgeted: zero,
  months: {} as Record<string, { income: Big; expense: Big }>,
  estimatedClicks: zero,
  estimatedImpressions: zero,
  reachImpressions: zero,
  reachRate: zero,
  maxReachRate: zero,
};

function sumSummary(a: SummaryFields, b: SummaryFields): SummaryFields {
  const months = { ...a.months };

  Object.keys(b.months).map((monthKey) => {
    const { income, expense } = b.months[monthKey];

    if (monthKey in months) {
      months[monthKey].income = months[monthKey].income.plus(income);
      months[monthKey].expense = months[monthKey].expense.plus(expense);
    } else {
      months[monthKey] = {
        income,
        expense,
      };
    }
  });

  return {
    clientGrossTotal: a.clientGrossTotal.plus(b.clientGrossTotal),
    clientTotal: a.clientTotal.plus(b.clientTotal),
    clientTotalBreakdown: sumBreakdown(a.clientTotalBreakdown, b.clientTotalBreakdown),
    providerTotal: a.providerTotal.plus(b.providerTotal),
    providerTotalBreakdown: sumBreakdown(a.providerTotalBreakdown, b.providerTotalBreakdown),
    budgeted: a.budgeted.plus(b.budgeted),
    months,
    estimatedClicks: a.estimatedClicks.plus(b.estimatedClicks),
    estimatedImpressions: a.estimatedImpressions.plus(b.estimatedImpressions),
    reachImpressions: a.reachImpressions.plus(b.reachImpressions),
    reachRate: a.reachRate.plus(b.reachRate),
    maxReachRate: a.maxReachRate.gt(b.maxReachRate) ? a.maxReachRate : b.maxReachRate,
  };
}

function planRowSummary(row: PlanRow): SummaryFields {
  const reachRate = parseBig(row.reach);
  const clientTotal = parseBig(row.sums.target.client_net_total);
  const providerTotal = parseBig(row.sums.target.provider_net_total);

  let clientTotalBreakdown: TotalBreakdown;
  let providerTotalBreakdown: TotalBreakdown;

  if (row.supplier?.activity.is_fee) {
    clientTotalBreakdown = { ...zeroBreakdown, fee: clientTotal };
    providerTotalBreakdown = { ...zeroBreakdown, fee: providerTotal };
  } else if (row.kind === 'MEDIA') {
    clientTotalBreakdown = { ...zeroBreakdown, media: clientTotal };
    providerTotalBreakdown = { ...zeroBreakdown, media: providerTotal };
  } else {
    clientTotalBreakdown = { ...zeroBreakdown, service: clientTotal };
    providerTotalBreakdown = { ...zeroBreakdown, service: providerTotal };
  }

  return {
    clientGrossTotal: parseBig(row.sums.planned.client_gross_total),
    clientTotal,
    clientTotalBreakdown,
    providerTotal,
    providerTotalBreakdown,
    budgeted: parseBig(row.budgeted),
    months: Object.keys(row.months).reduce(
      (acc, monthKey) => {
        acc[monthKey] = {
          income: parseBig(row.months[monthKey].target_client_net_total),
          expense: parseBig(row.months[monthKey].target_provider_net_total),
        };
        return acc;
      },
      {} as Record<string, { income: Big; expense: Big }>,
    ),
    estimatedClicks: parseBig(row.est_clicks),
    estimatedImpressions: parseBig(row.est_impressions),
    reachImpressions: parseBig(row.reach_impressions),
    reachRate,
    maxReachRate: reachRate,
  };
}

function rowSetSummaryFields(fields: SummaryFields): RowSetSummaryFields {
  const { clientGrossTotal, clientTotal, providerTotal } = fields;
  const discountRate = clientGrossTotal.eq(0)
    ? undefined
    : clientGrossTotal.minus(clientTotal).div(clientGrossTotal).mul(100);
  const revenue = clientTotal.minus(providerTotal);
  const margin = clientTotal.eq(0) ? undefined : revenue.div(clientTotal).mul(100);
  return {
    ...fields,
    discountRate,
    revenue,
    margin,
  };
}

export const rowSummaryAtom = atomFamily((id: number) =>
  atom((get): SummaryFields => {
    const row = get(baseRowAtom(id));
    return planRowSummary(row);
  }),
);

export const groupSummaryAtom = atomFamily((key: EntityKey) =>
  atom((get): RowSetSummaryFields => {
    const { entities, childrenMap } = get(gridAtom);
    const childKeys = childrenMap[key] || [];

    const childSummaries = childKeys.map((childKey) => {
      const ref = entities[childKey];
      if (ref.type === 'ROW') {
        return get(rowSummaryAtom(ref.id));
      }
      return get(groupSummaryAtom(childKey));
    });

    return rowSetSummaryFields(childSummaries.reduce(sumSummary, initialSummary));
  }),
);

const gridSummaryAtom = atom<RowSetSummaryFields>((get) => {
  const { rootIds, entities } = get(gridAtom);

  const rootSummaries = rootIds.map((key) => {
    const ref = entities[key];
    if (ref.type === 'ROW') {
      return get(rowSummaryAtom(ref.id));
    }
    return get(groupSummaryAtom(key));
  });

  return rowSetSummaryFields(rootSummaries.reduce(sumSummary, initialSummary));
});

export function useGridSummaryRows(): GridSummaryRow[] {
  const summary = useAtomValue(gridSummaryAtom);
  const { plan_reach_media_overlap, target_group_size } = usePlanSettings();
  return useMemo(() => {
    const { reachRate, maxReachRate, reachImpressions } = summary;
    let reach = plan_reach_media_overlap
      ? reachRate.minus(reachRate.mul(parseBig(plan_reach_media_overlap).div(100)))
      : undefined;

    if (reach && maxReachRate.gt(reach)) {
      reach = undefined;
    }

    const targetGroupSize = parseBig(target_group_size);

    const reachUniqueUsers = targetGroupSize && reach && !reach.eq(0) ? targetGroupSize.mul(reach.div(100)) : undefined;

    const reachFrequency =
      reachUniqueUsers && !reachUniqueUsers.eq(0) ? reachImpressions.mul(1000).div(reachUniqueUsers) : undefined;

    return [
      {
        summary: { ...summary, reach, reachUniqueUsers, reachFrequency },
      },
    ];
  }, [summary, plan_reach_media_overlap, target_group_size]);
}
